import { useEffect, useState } from "react";
import { useSnapshot } from "valtio";
import { toast } from "sonner";
import {
  adminStore,
  fetchApps,
  createApp,
  updateApp,
  deleteApp,
  regenerateAppSecret,
  type App,
} from "../stores/admin";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import {
  AlertCircle,
  Plus,
  Pencil,
  Trash2,
  Key,
  Copy,
  Eye,
  EyeOff,
} from "lucide-react";

interface AppFormData {
  name: string;
  redirectUris: string;
}

const defaultFormData: AppFormData = {
  name: "",
  redirectUris: "",
};

function AppDialog({
  open,
  onOpenChange,
  app,
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  app: App | null;
  onSubmit: (data: AppFormData) => Promise<void>;
}) {
  const [formData, setFormData] = useState<AppFormData>(defaultFormData);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (app) {
      setFormData({
        name: app.name,
        redirectUris: (app.redirectUris as string[])?.join("\n") ?? "",
      });
    } else {
      setFormData(defaultFormData);
    }
  }, [app, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onSubmit(formData);
      onOpenChange(false);
    } finally {
      setLoading(false);
    }
  };

  const isEdit = !!app;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit App" : "Create App"}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? `Update the configuration for ${app.name}`
              : "Create a new OAuth application"}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">App Name</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              placeholder="My Application"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="redirectUris">Redirect URIs</Label>
            <Textarea
              id="redirectUris"
              value={formData.redirectUris}
              onChange={(e) =>
                setFormData({ ...formData, redirectUris: e.target.value })
              }
              placeholder="https://myapp.com/callback&#10;http://localhost:3000/callback"
              rows={4}
            />
            <p className="text-xs text-muted-foreground">
              One URI per line. These are the allowed callback URLs for OAuth.
            </p>
          </div>

          {isEdit && (
            <div className="space-y-2">
              <Label>Client ID</Label>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-sm bg-muted px-3 py-2 rounded overflow-x-auto">
                  {app.clientId}
                </code>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => {
                    navigator.clipboard.writeText(app.clientId);
                    toast.success("Client ID copied");
                  }}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? "Saving..." : isEdit ? "Update" : "Create"} App
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function SecretDialog({
  open,
  onOpenChange,
  app,
  newSecret,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  app: App | null;
  newSecret: string | null;
}) {
  const [showSecret, setShowSecret] = useState(false);
  const secretToShow = newSecret ?? app?.clientSecret;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {newSecret ? "New Client Secret Generated" : "Client Secret"}
          </DialogTitle>
          <DialogDescription>
            {newSecret
              ? "Save this secret now. You won't be able to see it again."
              : "Your client secret for API authentication."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Client ID</Label>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-sm bg-muted px-3 py-2 rounded overflow-x-auto">
                {app?.clientId}
              </code>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => {
                  if (app?.clientId) {
                    navigator.clipboard.writeText(app.clientId);
                    toast.success("Client ID copied");
                  }
                }}
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Client Secret</Label>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-sm bg-muted px-3 py-2 rounded overflow-x-auto font-mono">
                {showSecret ? secretToShow : "••••••••••••••••••••••••"}
              </code>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => setShowSecret(!showSecret)}
              >
                {showSecret ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => {
                  if (secretToShow) {
                    navigator.clipboard.writeText(secretToShow);
                    toast.success("Client secret copied");
                  }
                }}
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={() => onOpenChange(false)}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminApps() {
  const { apps, appsLoading, error } = useSnapshot(adminStore);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingApp, setEditingApp] = useState<App | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [appToDelete, setAppToDelete] = useState<App | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [regenerateDialogOpen, setRegenerateDialogOpen] = useState(false);
  const [appToRegenerate, setAppToRegenerate] = useState<App | null>(null);
  const [regenerateLoading, setRegenerateLoading] = useState(false);
  const [secretDialogOpen, setSecretDialogOpen] = useState(false);
  const [secretApp, setSecretApp] = useState<App | null>(null);
  const [newSecret, setNewSecret] = useState<string | null>(null);

  useEffect(() => {
    fetchApps();
  }, []);

  const handleCreateApp = async (data: AppFormData) => {
    try {
      const redirectUris = data.redirectUris
        .split("\n")
        .map((uri) => uri.trim())
        .filter(Boolean);

      const createdApp = await createApp({
        name: data.name,
        redirectUris,
      });
      toast.success("App created successfully");

      // Show the secret dialog with the new app's credentials
      if (createdApp) {
        setSecretApp(createdApp as App);
        setNewSecret(createdApp.clientSecret);
        setSecretDialogOpen(true);
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to create app");
      throw e;
    }
  };

  const handleUpdateApp = async (data: AppFormData) => {
    if (!editingApp) return;
    try {
      const redirectUris = data.redirectUris
        .split("\n")
        .map((uri) => uri.trim())
        .filter(Boolean);

      await updateApp(editingApp.id, {
        name: data.name,
        redirectUris,
      });
      toast.success("App updated successfully");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to update app");
      throw e;
    }
  };

  const handleDeleteApp = async () => {
    if (!appToDelete) return;
    setDeleteLoading(true);
    try {
      await deleteApp(appToDelete.id);
      toast.success("App deleted successfully");
      setDeleteDialogOpen(false);
      setAppToDelete(null);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to delete app");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleRegenerateSecret = async () => {
    if (!appToRegenerate) return;
    setRegenerateLoading(true);
    try {
      const secret = await regenerateAppSecret(appToRegenerate.id);
      toast.success("Secret regenerated successfully");
      setRegenerateDialogOpen(false);

      // Show the new secret
      setSecretApp(appToRegenerate);
      setNewSecret(secret);
      setSecretDialogOpen(true);

      setAppToRegenerate(null);
    } catch (e) {
      toast.error(
        e instanceof Error ? e.message : "Failed to regenerate secret",
      );
    } finally {
      setRegenerateLoading(false);
    }
  };

  const openEditDialog = (app: App) => {
    setEditingApp(app);
    setDialogOpen(true);
  };

  const openCreateDialog = () => {
    setEditingApp(null);
    setDialogOpen(true);
  };

  const openDeleteDialog = (app: App) => {
    setAppToDelete(app);
    setDeleteDialogOpen(true);
  };

  const openRegenerateDialog = (app: App) => {
    setAppToRegenerate(app);
    setRegenerateDialogOpen(true);
  };

  return (
    <div className="flex h-full flex-col">
      {/* Fixed Header */}
      <header className="shrink-0 border-b bg-background px-4 py-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Apps</h1>
            <p className="mt-2 text-muted-foreground">
              Manage OAuth applications and API keys
            </p>
          </div>
          <Button onClick={openCreateDialog}>
            <Plus className="h-4 w-4 mr-2" />
            Create App
          </Button>
        </div>
      </header>

      {/* Scrollable Content */}
      <main className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
          {/* Error State */}
          {error && (
            <Alert variant="destructive" className="mb-6">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Apps Table */}
          <Card>
            <CardHeader>
              <CardTitle>All Apps ({apps.length})</CardTitle>
            </CardHeader>
            <CardContent>
              {appsLoading ? (
                <div className="space-y-2">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Skeleton key={i} className="h-12" />
                  ))}
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Client ID</TableHead>
                      <TableHead>Redirect URIs</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {apps.length > 0 ? (
                      apps.map((app) => (
                        <TableRow key={app.id}>
                          <TableCell className="font-medium">
                            {app.name}
                          </TableCell>
                          <TableCell>
                            <code className="text-sm bg-muted px-2 py-1 rounded">
                              {app.clientId}
                            </code>
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {(app.redirectUris as string[])?.length || 0}{" "}
                            configured
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                title="Regenerate Secret"
                                onClick={() => openRegenerateDialog(app as App)}
                              >
                                <Key className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openEditDialog(app as App)}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-destructive"
                                onClick={() => openDeleteDialog(app as App)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={4}
                          className="text-center text-muted-foreground py-8"
                        >
                          No apps found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      {/* App Dialog */}
      <AppDialog
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) setEditingApp(null);
        }}
        app={editingApp}
        onSubmit={editingApp ? handleUpdateApp : handleCreateApp}
      />

      {/* Secret Dialog */}
      <SecretDialog
        open={secretDialogOpen}
        onOpenChange={(open) => {
          setSecretDialogOpen(open);
          if (!open) {
            setSecretApp(null);
            setNewSecret(null);
          }
        }}
        app={secretApp}
        newSecret={newSecret}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete App"
        description={`Are you sure you want to delete "${appToDelete?.name}"? This will revoke all access tokens and cannot be undone.`}
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={handleDeleteApp}
        loading={deleteLoading}
      />

      {/* Regenerate Secret Confirmation Dialog */}
      <ConfirmDialog
        open={regenerateDialogOpen}
        onOpenChange={setRegenerateDialogOpen}
        title="Regenerate Secret"
        description={`Are you sure you want to regenerate the secret for "${appToRegenerate?.name}"? The old secret will stop working immediately.`}
        confirmLabel="Regenerate"
        variant="destructive"
        onConfirm={handleRegenerateSecret}
        loading={regenerateLoading}
      />
    </div>
  );
}
